import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../firebase';
import { getUserByEmail } from '../api/users';
import type { User } from '../types/users';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  firestoreUser: User | null;
  user: User | null; // Alias for firestoreUser for backward compatibility
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  firestoreUser: null,
  user: null,
  loading: true,
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [firestoreUser, setFirestoreUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to get cached user data from localStorage
  const getCachedUserData = (uid: string): User | null => {
    try {
      const cached = localStorage.getItem(`user_${uid}`);
      if (cached) {
        const userData = JSON.parse(cached);
        // Check if cache is not older than 24 hours
        const cacheTime = userData._cachedAt;
        const now = Date.now();
        if (now - cacheTime < 24 * 60 * 60 * 1000) { // 24 hours
          return userData;
        } else {
          localStorage.removeItem(`user_${uid}`);
        }
      }
    } catch (error) {
      console.error('Error reading cached user data:', error);
    }
    return null;
  };

  // Function to cache user data in localStorage
  const cacheUserData = (uid: string, userData: User) => {
    try {
      const dataToCache = {
        ...userData,
        _cachedAt: Date.now()
      };
      localStorage.setItem(`user_${uid}`, JSON.stringify(dataToCache));
    } catch (error) {
      console.error('Error caching user data:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      setLoading(true);
      
      if (fbUser) {
        try {
          // Try to get cached user data first
          const cachedUserData = getCachedUserData(fbUser.uid);
          
          if (cachedUserData) {
            setFirestoreUser(cachedUserData);
          } else {
            // Fetch fresh user data if not cached
            if (fbUser.email) {
              const userProfile = await getUserByEmail(fbUser.email);
              if (userProfile) {
                setFirestoreUser(userProfile);
                // Cache the user data
                cacheUserData(fbUser.uid, userProfile);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setFirestoreUser(null);
        }
      } else {
        setFirestoreUser(null);
      }
      
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const logout = async () => {
    // Clear user data cache on sign out
    if (firebaseUser) {
      localStorage.removeItem(`user_${firebaseUser.uid}`);
    }
    
    setFirestoreUser(null);
    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ firebaseUser, firestoreUser, user: firestoreUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 